import Dexie from 'dexie';
import { v4 as uuidv4 } from 'uuid';
import { createDefaultPreferences, createDefaultStats, habitInstanceKeysFor } from '@trackmind/core';
import { deletionEffect } from '@trackmind/core';
class TrackMindDatabase extends Dexie {
    tasks;
    projects;
    schedules;
    goals;
    notes;
    lists;
    listInstances;
    habitInstances;
    journalEntries;
    moodCheckIns;
    /**
     * Generated period reviews.
     *
     * These used to live in React state and nowhere else, so the notes a user
     * wrote on a review were gone the moment the page reloaded. The generated
     * *content* is derivable from tasks and habits, but the notes are not, and
     * a review is also a snapshot: regenerating March's review in June would
     * report different numbers if any of that work has since been edited or
     * deleted. Both reasons make it content to keep, not a cache to rebuild.
     */
    reviews;
    /** Strength-training log, weigh-ins and the settings singleton (v7). */
    fitnessWorkouts;
    fitnessBodyweights;
    fitnessSettings;
    preferences;
    stats;
    pendingChanges;
    tombstones;
    metadata;
    constructor() {
        super('TrackMindDB');
        // Handle blocked upgrades (e.g., another tab has the DB open with old version)
        this.on('blocked', () => {
            console.warn('[IndexedDB] Database upgrade blocked - waiting for other tabs to close');
            // Don't alert immediately - the versionchange handler in other tabs should handle it
        });
        // When another tab needs to upgrade the database, this tab should reload
        // to get the new code and schema
        this.on('versionchange', () => {
            console.log('[IndexedDB] New version detected in another tab, reloading...');
            this.close();
            // Notify user and reload to get new version
            if (typeof window !== 'undefined') {
                // Small delay to allow the close to complete
                setTimeout(() => {
                    window.location.reload();
                }, 100);
            }
        });
        this.version(1).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            metadata: 'key',
        });
        // Version 2: Ensure all tables exist (no schema change, just trigger upgrade for older DBs)
        this.version(2).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            metadata: 'key',
        });
        // Version 3: Add tombstones table for deletion sync
        this.version(3).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            tombstones: 'id, entityType, entityId, deletedAt',
            metadata: 'key',
        });
        // Version 4: Add journal entries table
        this.version(4).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            journalEntries: 'id, date, timeOfDay, *linked_project_ids, *linked_habit_ids, *tags',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            tombstones: 'id, entityType, entityId, deletedAt',
            metadata: 'key',
        });
        // Version 5: Add mood check-ins table
        this.version(5).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            journalEntries: 'id, date, timeOfDay, *linked_project_ids, *linked_habit_ids, *tags',
            moodCheckIns: 'id, date, timestamp',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            tombstones: 'id, entityType, entityId, deletedAt',
            metadata: 'key',
        });
        // Version 6: reviews become persistent. Purely additive — Dexie creates
        // the new store and leaves every existing one untouched, so no upgrade
        // function is needed and no existing row is rewritten.
        this.version(6).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            journalEntries: 'id, date, timeOfDay, *linked_project_ids, *linked_habit_ids, *tags',
            moodCheckIns: 'id, date, timestamp',
            reviews: 'id, review_type, date',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            tombstones: 'id, entityType, entityId, deletedAt',
            metadata: 'key',
        });
        // Version 7: fitness. Purely additive, exactly like v6 — three new
        // stores, no upgrade function, no existing row touched.
        this.version(7).stores({
            tasks: 'id, project_id, status, deadline, scheduled_for, is_habit, *tags',
            projects: 'id, status, parent_id',
            schedules: 'date',
            goals: 'id, time_horizon, category, status',
            notes: 'id, folder_path, *tags',
            lists: 'id, list_type',
            listInstances: 'id, list_id',
            habitInstances: '_key, habit_id, date',
            journalEntries: 'id, date, timeOfDay, *linked_project_ids, *linked_habit_ids, *tags',
            moodCheckIns: 'id, date, timestamp',
            reviews: 'id, review_type, date',
            fitnessWorkouts: 'id, date, status',
            fitnessBodyweights: 'id, date',
            fitnessSettings: 'id',
            preferences: '_id',
            stats: '_id',
            pendingChanges: 'id, type, timestamp',
            tombstones: 'id, entityType, entityId, deletedAt',
            metadata: 'key',
        });
    }
}
export class IndexedDBStorage {
    db;
    constructor() {
        this.db = new TrackMindDatabase();
    }
    async initialize() {
        try {
            console.log('[IndexedDB] Opening database...');
            await this.db.open();
            console.log('[IndexedDB] Database opened successfully, version:', this.db.verno);
        }
        catch (error) {
            console.error('[IndexedDB] Failed to open database:', error);
            throw error;
        }
        // Initialize preferences if not exists
        try {
            const prefs = await this.db.preferences.get('default');
            if (!prefs) {
                await this.db.preferences.put({
                    ...createDefaultPreferences(),
                    _id: 'default',
                });
            }
        }
        catch (error) {
            console.warn('[IndexedDB] Failed to initialize preferences:', error);
        }
        // Initialize stats if not exists
        try {
            const stats = await this.db.stats.get('default');
            if (!stats) {
                await this.db.stats.put({
                    ...createDefaultStats(),
                    _id: 'default',
                });
            }
        }
        catch (error) {
            console.warn('[IndexedDB] Failed to initialize stats:', error);
        }
    }
    async close() {
        this.db.close();
    }
    // Tasks
    async getTasks() {
        const tasks = await this.db.tasks.toArray();
        // Normalize tasks to ensure all required fields have values
        // This handles legacy data that may be missing newer fields like is_pinned
        return tasks.map(task => ({
            ...task,
            is_pinned: task.is_pinned ?? false,
            pin_type: task.pin_type ?? 'soft',
            is_paused: task.is_paused ?? false,
            is_archived: task.is_archived ?? false,
            archived_at: task.archived_at ?? null,
            needs_review: task.needs_review ?? false,
        }));
    }
    async getTask(id) {
        const task = await this.db.tasks.get(id);
        if (!task)
            return null;
        // Normalize to ensure all required fields have values
        return {
            ...task,
            is_pinned: task.is_pinned ?? false,
            pin_type: task.pin_type ?? 'soft',
            is_paused: task.is_paused ?? false,
            is_archived: task.is_archived ?? false,
            archived_at: task.archived_at ?? null,
            needs_review: task.needs_review ?? false,
        };
    }
    async getTasksByProject(projectId) {
        const tasks = await this.db.tasks.where('project_id').equals(projectId).toArray();
        return tasks.map(task => ({
            ...task,
            is_pinned: task.is_pinned ?? false,
            pin_type: task.pin_type ?? 'soft',
            is_paused: task.is_paused ?? false,
            is_archived: task.is_archived ?? false,
            archived_at: task.archived_at ?? null,
            needs_review: task.needs_review ?? false,
        }));
    }
    async getTasksByStatus(status) {
        const tasks = await this.db.tasks.where('status').equals(status).toArray();
        return tasks.map(task => ({
            ...task,
            is_pinned: task.is_pinned ?? false,
            pin_type: task.pin_type ?? 'soft',
            is_paused: task.is_paused ?? false,
            is_archived: task.is_archived ?? false,
            archived_at: task.archived_at ?? null,
            needs_review: task.needs_review ?? false,
        }));
    }
    async saveTask(task) {
        const existing = await this.db.tasks.get(task.id);
        await this.db.tasks.put(task);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'task',
            operation: existing ? 'update' : 'create',
            entityId: task.id,
            timestamp: new Date().toISOString(),
            data: task,
        });
    }
    async saveTasks(tasks) {
        await this.db.tasks.bulkPut(tasks);
        for (const task of tasks) {
            await this.addPendingChange({
                id: uuidv4(),
                type: 'task',
                operation: 'update',
                entityId: task.id,
                timestamp: new Date().toISOString(),
            });
        }
    }
    async deleteTask(id) {
        const now = new Date().toISOString();
        // A habit's completion history goes with it, tombstoned so the deletion
        // reaches other devices — what the assistant's delete has always done.
        // Left behind, the instances were orphans still counted as active days.
        const instances = await this.db.habitInstances.where('habit_id').equals(id).toArray();
        for (const key of habitInstanceKeysFor(instances, [id])) {
            await this.db.habitInstances.delete(key);
            await this.recordDeletion('habit_instance', key, now);
        }
        await this.db.tasks.delete(id);
        await this.recordDeletion('task', id, now);
    }
    // Projects
    async getProjects() {
        return this.db.projects.toArray();
    }
    async getProject(id) {
        return (await this.db.projects.get(id)) ?? null;
    }
    async saveProject(project) {
        const existing = await this.db.projects.get(project.id);
        await this.db.projects.put(project);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'project',
            operation: existing ? 'update' : 'create',
            entityId: project.id,
            timestamp: new Date().toISOString(),
            data: project,
        });
    }
    async deleteProject(id) {
        const now = new Date().toISOString();
        await this.db.projects.delete(id);
        await this.recordDeletion('project', id, now);
    }
    // Schedules
    async getSchedule(date) {
        return (await this.db.schedules.get(date)) ?? null;
    }
    async getSchedules(startDate, endDate) {
        return this.db.schedules
            .where('date')
            .between(startDate, endDate, true, true)
            .toArray();
    }
    async saveSchedule(schedule) {
        await this.db.schedules.put(schedule);
        await this.clearTombstone('schedule', schedule.date);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'schedule',
            operation: 'update',
            entityId: schedule.date,
            timestamp: new Date().toISOString(),
        });
    }
    async saveSchedules(schedules) {
        await this.db.schedules.bulkPut(schedules);
        for (const schedule of schedules) {
            await this.addPendingChange({
                id: uuidv4(),
                type: 'schedule',
                operation: 'update',
                entityId: schedule.date,
                timestamp: new Date().toISOString(),
            });
        }
    }
    async deleteSchedule(date) {
        const now = new Date().toISOString();
        await this.db.schedules.delete(date);
        await this.recordDeletion('schedule', date, now);
    }
    // Goals
    async getGoals() {
        return this.db.goals.toArray();
    }
    async getGoal(id) {
        return (await this.db.goals.get(id)) ?? null;
    }
    async saveGoal(goal) {
        await this.db.goals.put(goal);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'goal',
            operation: 'update',
            entityId: goal.id,
            timestamp: new Date().toISOString(),
        });
    }
    async deleteGoal(id) {
        const now = new Date().toISOString();
        await this.db.goals.delete(id);
        await this.recordDeletion('goal', id, now);
    }
    // Notes
    async getNotes() {
        return this.db.notes.toArray();
    }
    async getNote(id) {
        return (await this.db.notes.get(id)) ?? null;
    }
    async getNotesByFolder(folder) {
        return this.db.notes.where('folder_path').equals(folder).toArray();
    }
    async saveNote(note) {
        await this.db.notes.put(note);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'note',
            operation: 'update',
            entityId: note.id,
            timestamp: new Date().toISOString(),
        });
    }
    async deleteNote(id) {
        const now = new Date().toISOString();
        await this.db.notes.delete(id);
        await this.recordDeletion('note', id, now);
    }
    // Lists
    async getLists() {
        return this.db.lists.toArray();
    }
    async getList(id) {
        return (await this.db.lists.get(id)) ?? null;
    }
    async saveList(list) {
        await this.db.lists.put(list);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'list',
            operation: 'update',
            entityId: list.id,
            timestamp: new Date().toISOString(),
        });
    }
    async deleteList(id) {
        const now = new Date().toISOString();
        await this.db.lists.delete(id);
        await this.recordDeletion('list', id, now);
    }
    // List Instances
    async getListInstances() {
        return this.db.listInstances.toArray();
    }
    async getListInstance(id) {
        return (await this.db.listInstances.get(id)) ?? null;
    }
    async saveListInstance(instance) {
        await this.db.listInstances.put(instance);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'list_instance',
            operation: 'update',
            entityId: instance.id,
            timestamp: new Date().toISOString(),
        });
    }
    async deleteListInstance(id) {
        const now = new Date().toISOString();
        await this.db.listInstances.delete(id);
        await this.recordDeletion('list_instance', id, now);
    }
    // Habit Instances
    async getHabitInstance(taskId, date) {
        const key = `${taskId}:${date}`;
        const result = await this.db.habitInstances.get(key);
        if (!result)
            return null;
        const { _key, ...instance } = result;
        return instance;
    }
    async getHabitInstances(date) {
        const results = await this.db.habitInstances.where('date').equals(date).toArray();
        return results.map(({ _key, ...instance }) => instance);
    }
    async getHabitInstancesByHabit(habitId, startDate, endDate) {
        let results = await this.db.habitInstances.where('habit_id').equals(habitId).toArray();
        // Filter by date range if provided
        if (startDate) {
            results = results.filter(r => r.date >= startDate);
        }
        if (endDate) {
            results = results.filter(r => r.date <= endDate);
        }
        return results.map(({ _key, ...instance }) => instance);
    }
    async getAllHabitInstances() {
        const results = await this.db.habitInstances.toArray();
        return results.map(({ _key, ...instance }) => instance);
    }
    async saveHabitInstance(instance) {
        const key = `${instance.habit_id}:${instance.date}`;
        await this.db.habitInstances.put({ ...instance, _key: key });
        await this.clearTombstone('habit_instance', key);
        // Track habit instance changes for sync
        await this.addPendingChange({
            id: uuidv4(),
            type: 'habit_instance',
            operation: 'update',
            entityId: key,
            timestamp: new Date().toISOString(),
            data: instance,
        });
    }
    async deleteHabitInstance(habitId, date) {
        const now = new Date().toISOString();
        const key = `${habitId}:${date}`;
        await this.db.habitInstances.delete(key);
        // Tombstoned like every other delete, so undoing a habit tick travels to
        // your other devices instead of the record reappearing on the next sync.
        await this.recordDeletion('habit_instance', key, now);
    }
    // Journal Entries
    async getJournalEntries() {
        try {
            return await this.db.journalEntries.toArray();
        }
        catch (error) {
            // Table might not exist yet during migration from older schema
            console.warn('[IndexedDB] getJournalEntries failed, returning empty array:', error);
            return [];
        }
    }
    async getJournalEntry(id) {
        return (await this.db.journalEntries.get(id)) ?? null;
    }
    async getJournalEntriesByDate(date) {
        return this.db.journalEntries.where('date').equals(date).toArray();
    }
    async getJournalEntriesByDateRange(startDate, endDate) {
        return this.db.journalEntries
            .where('date')
            .between(startDate, endDate, true, true)
            .toArray();
    }
    async saveJournalEntry(entry) {
        const existing = await this.db.journalEntries.get(entry.id);
        await this.db.journalEntries.put(entry);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'journal',
            operation: existing ? 'update' : 'create',
            entityId: entry.id,
            timestamp: new Date().toISOString(),
            data: entry,
        });
    }
    async deleteJournalEntry(id) {
        const now = new Date().toISOString();
        await this.db.journalEntries.delete(id);
        await this.recordDeletion('journal', id, now);
    }
    // Reviews
    async getReviews() {
        try {
            return await this.db.reviews.toArray();
        }
        catch (error) {
            // The store only exists from schema v6. A browser that hasn't run the
            // upgrade yet must read as empty, not throw — same guard the journal
            // table carries for the same reason.
            console.warn('[IndexedDB] getReviews failed, returning empty array:', error);
            return [];
        }
    }
    async saveReview(review) {
        const existing = await this.db.reviews.get(review.id);
        await this.db.reviews.put(review);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'review',
            operation: existing ? 'update' : 'create',
            entityId: review.id,
            timestamp: new Date().toISOString(),
            data: review,
        });
    }
    async deleteReview(id) {
        const now = new Date().toISOString();
        await this.db.reviews.delete(id);
        await this.recordDeletion('review', id, now);
    }
    // Fitness. Every read is guarded the way reviews are: a browser that has
    // not run the v7 upgrade yet must read as "no workouts", not throw.
    async getFitnessWorkouts() {
        try {
            return await this.db.fitnessWorkouts.toArray();
        }
        catch (error) {
            console.warn('[IndexedDB] getFitnessWorkouts failed, returning empty array:', error);
            return [];
        }
    }
    async saveFitnessWorkout(workout) {
        const existing = await this.db.fitnessWorkouts.get(workout.id);
        await this.db.fitnessWorkouts.put(workout);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'fitness_workout',
            operation: existing ? 'update' : 'create',
            entityId: workout.id,
            timestamp: new Date().toISOString(),
            data: workout,
        });
    }
    async deleteFitnessWorkout(id) {
        const now = new Date().toISOString();
        await this.db.fitnessWorkouts.delete(id);
        await this.recordDeletion('fitness_workout', id, now);
    }
    async getFitnessBodyweights() {
        try {
            return await this.db.fitnessBodyweights.toArray();
        }
        catch (error) {
            console.warn('[IndexedDB] getFitnessBodyweights failed, returning empty array:', error);
            return [];
        }
    }
    async saveFitnessBodyweight(entry) {
        const existing = await this.db.fitnessBodyweights.get(entry.id);
        await this.db.fitnessBodyweights.put(entry);
        await this.clearTombstone('fitness_bodyweight', entry.id);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'fitness_bodyweight',
            operation: existing ? 'update' : 'create',
            entityId: entry.id,
            timestamp: new Date().toISOString(),
            data: entry,
        });
    }
    async deleteFitnessBodyweight(id) {
        const now = new Date().toISOString();
        await this.db.fitnessBodyweights.delete(id);
        await this.recordDeletion('fitness_bodyweight', id, now);
    }
    async getFitnessSettings() {
        try {
            return (await this.db.fitnessSettings.toArray())[0] ?? null;
        }
        catch (error) {
            console.warn('[IndexedDB] getFitnessSettings failed, returning null:', error);
            return null;
        }
    }
    async saveFitnessSettings(settings) {
        const existing = await this.db.fitnessSettings.get(settings.id);
        await this.db.fitnessSettings.put(settings);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'fitness_settings',
            operation: existing ? 'update' : 'create',
            entityId: settings.id,
            timestamp: new Date().toISOString(),
            data: settings,
        });
    }
    // Mood Check-ins
    async getMoodCheckIns() {
        try {
            return await this.db.moodCheckIns.toArray();
        }
        catch (error) {
            // Table might not exist yet during migration from older schema
            console.warn('[IndexedDB] getMoodCheckIns failed, returning empty array:', error);
            return [];
        }
    }
    async getMoodCheckIn(id) {
        try {
            return (await this.db.moodCheckIns.get(id)) ?? null;
        }
        catch (error) {
            console.warn('[IndexedDB] getMoodCheckIn failed:', error);
            return null;
        }
    }
    async getMoodCheckInsByDate(date) {
        try {
            return await this.db.moodCheckIns.where('date').equals(date).toArray();
        }
        catch (error) {
            console.warn('[IndexedDB] getMoodCheckInsByDate failed:', error);
            return [];
        }
    }
    async getMoodCheckInsByDateRange(startDate, endDate) {
        try {
            return await this.db.moodCheckIns
                .where('date')
                .between(startDate, endDate, true, true)
                .toArray();
        }
        catch (error) {
            console.warn('[IndexedDB] getMoodCheckInsByDateRange failed:', error);
            return [];
        }
    }
    async saveMoodCheckIn(moodCheckIn) {
        const existing = await this.db.moodCheckIns.get(moodCheckIn.id);
        await this.db.moodCheckIns.put(moodCheckIn);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'mood_checkin',
            operation: existing ? 'update' : 'create',
            entityId: moodCheckIn.id,
            timestamp: new Date().toISOString(),
            data: moodCheckIn,
        });
    }
    async deleteMoodCheckIn(id) {
        const now = new Date().toISOString();
        await this.db.moodCheckIns.delete(id);
        await this.recordDeletion('mood_checkin', id, now);
    }
    // Preferences
    async getPreferences() {
        const prefs = await this.db.preferences.get('default');
        if (!prefs)
            return createDefaultPreferences();
        const { _id, ...preferences } = prefs;
        return preferences;
    }
    async savePreferences(preferences) {
        // Stamp updated_at on every local write so mergeSync can pick the
        // newer side when both devices have diverged.
        const stamped = { ...preferences, updated_at: new Date().toISOString() };
        await this.db.preferences.put({ ...stamped, _id: 'default' });
        await this.addPendingChange({
            id: uuidv4(),
            type: 'preferences',
            operation: 'update',
            entityId: 'default',
            timestamp: new Date().toISOString(),
        });
    }
    // Stats
    async getStats() {
        const stats = await this.db.stats.get('default');
        if (!stats)
            return createDefaultStats();
        const { _id, ...userStats } = stats;
        return userStats;
    }
    async saveStats(stats) {
        // Stamp updated_at on every local write so mergeSync can pick the
        // newer side when both devices have diverged.
        const stamped = { ...stats, updated_at: new Date().toISOString() };
        await this.db.stats.put({ ...stamped, _id: 'default' });
        await this.addPendingChange({
            id: uuidv4(),
            type: 'stats',
            operation: 'update',
            entityId: 'default',
            timestamp: new Date().toISOString(),
        });
    }
    // Sync Metadata
    async getLastSyncTime() {
        try {
            const meta = await this.db.metadata.get('lastSyncTime');
            return meta ? new Date(meta.value) : null;
        }
        catch (error) {
            console.warn('[Storage] getLastSyncTime failed:', error);
            return null;
        }
    }
    async setLastSyncTime(time) {
        try {
            await this.db.metadata.put({ key: 'lastSyncTime', value: time.toISOString() });
        }
        catch (error) {
            console.warn('[Storage] setLastSyncTime failed:', error);
        }
    }
    async getPendingChanges() {
        try {
            return await this.db.pendingChanges.orderBy('timestamp').toArray();
        }
        catch (error) {
            console.warn('[Storage] getPendingChanges failed, returning empty array:', error);
            return [];
        }
    }
    async addPendingChange(change) {
        try {
            await this.db.pendingChanges.put(change);
        }
        catch (error) {
            console.warn('[Storage] addPendingChange failed:', error);
        }
    }
    async clearPendingChanges(changeIds) {
        try {
            if (changeIds.length > 0) {
                await this.db.pendingChanges.bulkDelete(changeIds);
            }
        }
        catch (error) {
            console.warn('[Storage] clearPendingChanges failed:', error);
        }
    }
    // Tombstone Management
    async getTombstones() {
        try {
            return await this.db.tombstones.toArray();
        }
        catch (error) {
            console.warn('[Storage] getTombstones failed, returning empty array:', error);
            return [];
        }
    }
    /**
     * Record that something was deleted.
     *
     * Every delete on this adapter goes through here, and the two records come
     * from `deletionEffect` in core — the same function the MCP server uses, so
     * a deletion made by the assistant and one made by tapping Delete leave
     * identical evidence behind.
     *
     * It was written out by hand in eleven methods before, which is how the
     * server ended up not writing tombstones at all: there was no single thing
     * to call, only a shape to copy, and the copy was never made. The pair
     * matters as a pair — a pending change without a tombstone syncs the
     * deletion once and lets it come back on the next pull.
     */
    async recordDeletion(entityType, entityId, now) {
        const { tombstones, pendingChanges } = deletionEffect(entityType, [entityId], now);
        for (const change of pendingChanges)
            await this.addPendingChange(change);
        for (const tombstone of tombstones)
            await this.addTombstone(tombstone);
    }
    async addTombstone(tombstone) {
        try {
            await this.db.tombstones.put(tombstone);
        }
        catch (error) {
            console.warn('[Storage] addTombstone failed:', error);
        }
    }
    /**
     * A natural-key row saved again after it was deleted — a habit re-ticked
     * after an undo, a weigh-in re-logged — retires its own tombstone, so the
     * deletion stops travelling to other devices.
     */
    async clearTombstone(entityType, entityId) {
        try {
            await this.db.tombstones
                .where('entityId').equals(entityId)
                .filter((t) => t.entityType === entityType)
                .delete();
        }
        catch (error) {
            console.warn('[Storage] clearTombstone failed:', error);
        }
    }
    async importTombstones(tombstones) {
        try {
            // Merge with existing tombstones (avoid duplicates by entityType+entityId)
            const existing = await this.getTombstones();
            const existingKeys = new Set(existing.map(t => `${t.entityType}:${t.entityId}`));
            const newTombstones = tombstones.filter(t => !existingKeys.has(`${t.entityType}:${t.entityId}`));
            if (newTombstones.length > 0) {
                await this.db.tombstones.bulkPut(newTombstones);
            }
        }
        catch (error) {
            console.warn('[Storage] importTombstones failed:', error);
        }
    }
    async cleanupTombstones(olderThanDays) {
        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
            const cutoffISO = cutoffDate.toISOString();
            const oldTombstones = await this.db.tombstones
                .where('deletedAt')
                .below(cutoffISO)
                .toArray();
            if (oldTombstones.length > 0) {
                await this.db.tombstones.bulkDelete(oldTombstones.map(t => t.id));
            }
            return oldTombstones.length;
        }
        catch (error) {
            console.warn('[Storage] cleanupTombstones failed:', error);
            return 0;
        }
    }
    // Bulk Operations
    async exportAll() {
        const [tasks, projects, schedules, goals, notes, lists, listInstances, habitInstancesRaw, journalEntries, moodCheckIns, reviews, fitnessWorkouts, fitnessBodyweights, fitnessSettings, preferences, stats, tombstones,] = await Promise.all([
            this.getTasks(),
            this.getProjects(),
            this.db.schedules.toArray(),
            this.getGoals(),
            this.getNotes(),
            this.getLists(),
            this.getListInstances(),
            this.db.habitInstances.toArray(),
            this.getJournalEntries(),
            this.getMoodCheckIns(),
            this.getReviews(),
            this.getFitnessWorkouts(),
            this.getFitnessBodyweights(),
            this.getFitnessSettings(),
            this.getPreferences(),
            this.getStats(),
            this.getTombstones(),
        ]);
        const habitInstances = habitInstancesRaw.map(({ _key, ...instance }) => instance);
        return {
            version: '1.0.0',
            exportedAt: new Date().toISOString(),
            tasks,
            projects,
            schedules,
            goals,
            notes,
            lists,
            listInstances,
            habitInstances,
            journalEntries,
            moodCheckIns,
            reviews,
            fitnessWorkouts,
            fitnessBodyweights,
            fitnessSettings: fitnessSettings ? [fitnessSettings] : [],
            preferences,
            stats,
            tombstones: {
                tombstones,
                cleanedUpBefore: undefined, // Will be set during cleanup
            },
        };
    }
    async importAll(data) {
        await this.db.transaction('rw', [
            this.db.tasks,
            this.db.projects,
            this.db.schedules,
            this.db.goals,
            this.db.notes,
            this.db.lists,
            this.db.listInstances,
            this.db.habitInstances,
            this.db.journalEntries,
            this.db.moodCheckIns,
            this.db.reviews,
            this.db.fitnessWorkouts,
            this.db.fitnessBodyweights,
            this.db.fitnessSettings,
            this.db.tombstones,
            this.db.preferences,
            this.db.stats,
        ], async () => {
            // Clear existing data
            await Promise.all([
                this.db.tasks.clear(),
                this.db.projects.clear(),
                this.db.schedules.clear(),
                this.db.goals.clear(),
                this.db.notes.clear(),
                this.db.lists.clear(),
                this.db.listInstances.clear(),
                this.db.habitInstances.clear(),
                this.db.journalEntries.clear(),
                this.db.moodCheckIns.clear(),
                this.db.reviews.clear(),
                this.db.fitnessWorkouts.clear(),
                this.db.fitnessBodyweights.clear(),
                this.db.fitnessSettings.clear(),
            ]);
            // Import new data (with fallbacks for older export formats)
            const habitInstances = (data.habitInstances || []).map(hi => ({
                ...hi,
                _key: `${hi.habit_id}:${hi.date}`,
            }));
            // Handle old format: convert separate habits array to tasks with is_habit=true
            const oldHabits = data.habits || [];
            const now = new Date().toISOString();
            const convertedHabits = oldHabits.map((habit) => ({
                id: habit.id,
                project_id: habit.project_id,
                parent_task_id: null,
                title: habit.title,
                description: habit.description || '',
                priority: habit.priority ?? 2,
                difficulty: habit.difficulty ?? 2,
                energy_level: habit.energy_level || 'medium',
                estimated_minutes: habit.estimated_minutes ?? 30,
                actual_minutes: null,
                status: habit.status || 'todo',
                dependencies: [],
                tags: habit.tags || [],
                deadline: null,
                scheduled_for: null,
                created_at: habit.created_at || now,
                updated_at: habit.updated_at || now,
                completed_at: null,
                is_habit: true,
                recurrence: habit.recurrence || { frequency: 'daily', days_of_week: [], time_of_day: null },
                is_paused: habit.is_paused ?? false,
                paused_until: habit.paused_until || null,
                is_archived: habit.is_archived ?? false,
                archived_at: habit.archived_at ?? null,
                needs_review: false,
                is_pinned: false,
                pin_type: 'soft',
            }));
            // Merge tasks with converted habits and normalize
            // Normalize imported tasks to ensure they have all required fields
            const normalizedTasks = (data.tasks || []).map((task) => ({
                ...task,
                is_pinned: task.is_pinned ?? false,
                pin_type: task.pin_type ?? 'soft',
                is_paused: task.is_paused ?? false,
                is_archived: task.is_archived ?? false,
                archived_at: task.archived_at ?? null,
                needs_review: task.needs_review ?? false,
            }));
            const allTasks = [...normalizedTasks, ...convertedHabits];
            await Promise.all([
                this.db.tasks.bulkPut(allTasks),
                this.db.projects.bulkPut(data.projects || []),
                this.db.schedules.bulkPut(data.schedules || []),
                this.db.goals.bulkPut(data.goals || []),
                this.db.notes.bulkPut(data.notes || []),
                this.db.lists.bulkPut(data.lists || []),
                this.db.listInstances.bulkPut(data.listInstances || []),
                this.db.habitInstances.bulkPut(habitInstances),
                this.db.journalEntries.bulkPut(data.journalEntries || []),
                this.db.moodCheckIns.bulkPut(data.moodCheckIns || []),
                // `|| []` and not `data.reviews!`: every export written before
                // reviews were persisted has no such key.
                this.db.reviews.bulkPut(data.reviews || []),
                // Same `|| []` for the same reason: nothing before 2.5 has these.
                this.db.fitnessWorkouts.bulkPut(data.fitnessWorkouts || []),
                this.db.fitnessBodyweights.bulkPut(data.fitnessBodyweights || []),
                this.db.fitnessSettings.bulkPut((data.fitnessSettings || []).slice(0, 1)),
            ]);
            // Stored as given — the merge already chose them, timestamps included.
            // Going through savePreferences/saveStats re-stamped `updated_at` to
            // now, so after every sync this device's copy looked newer than
            // anything it had pushed and won the next merge outright; and their
            // pending-change write failed inside this transaction (the table isn't
            // part of it), logging an error on every sync.
            if (data.preferences) {
                await this.db.preferences.put({ ...data.preferences, _id: 'default' });
            }
            if (data.stats) {
                await this.db.stats.put({ ...data.stats, _id: 'default' });
            }
        });
        // Import tombstones (merge, don't clear - we need to preserve local deletions)
        if (data.tombstones?.tombstones) {
            await this.importTombstones(data.tombstones.tombstones);
        }
    }
    async clearAll() {
        await this.db.transaction('rw', [
            this.db.tasks,
            this.db.projects,
            this.db.schedules,
            this.db.goals,
            this.db.notes,
            this.db.lists,
            this.db.listInstances,
            this.db.habitInstances,
            this.db.journalEntries,
            this.db.moodCheckIns,
            // Reviews were missing here, so "Clear all data" left them behind.
            this.db.reviews,
            this.db.fitnessWorkouts,
            this.db.fitnessBodyweights,
            this.db.fitnessSettings,
            this.db.pendingChanges,
            this.db.tombstones,
            this.db.metadata,
            // And these two: without them "Clear all data" reset XP, badges and
            // settings on screen, and a reload brought them all back.
            this.db.preferences,
            this.db.stats,
        ], async () => {
            await Promise.all([
                this.db.tasks.clear(),
                this.db.projects.clear(),
                this.db.schedules.clear(),
                this.db.goals.clear(),
                this.db.notes.clear(),
                this.db.lists.clear(),
                this.db.listInstances.clear(),
                this.db.habitInstances.clear(),
                this.db.journalEntries.clear(),
                this.db.moodCheckIns.clear(),
                this.db.reviews.clear(),
                this.db.fitnessWorkouts.clear(),
                this.db.fitnessBodyweights.clear(),
                this.db.fitnessSettings.clear(),
                this.db.pendingChanges.clear(),
                this.db.tombstones.clear(),
                this.db.metadata.clear(),
                this.db.preferences.clear(),
                this.db.stats.clear(),
            ]);
        });
    }
}
//# sourceMappingURL=indexeddb.js.map